import AutocompleteBox from "@/InputControls/AutocompleteBox";
import TextArea from "@/InputControls/TextArea";
import ComboBox from "@/InputControls/ComboBox";
import SelectableBoxes from "@/InputControls/SelectableBoxes";
import TextField from "@/InputControls/TextField";
import CompositeBoxForNumber from "@/InputControls/CompositeBoxForNumber";
import TextNode from "@/InputControls/TextNode";
import {currencyOptions, timePeriodOptions} from "@/config";
import CompositeBoxForRatio from "@/InputControls/CompositeBoxForRatio";
import CompositeBoxForRange from "@/InputControls/CompositeBoxForRange";
import {dateObjectToDateString, numberWithCommas, placeholderDateUnix} from "@/utils";
import MultiLevelChoices from "@/InputControls/MultiLevelChoices";
import moment from "moment";
import MultiObjBox from "@/InputControls/MultiObjBox";
import PersonCapacityBox from "@/InputControls/PersonCapacityBox";
import { escapeRegExp } from "../utils";

const getPropsForNumberField = function (decimal, positiveOnly) {
    function getSimplified(stringValue) {
        return stringValue.replace(/[,\s]/g, '');
    }

    function getParsedResult(stringValue) {
        let canParse = false;
        let parseResult;

        let reg;

        let test = decimal ? parseFloat(stringValue) : parseInt(stringValue);
        if (isFinite(test) && test.toString() === stringValue) {
            canParse = true;
            parseResult = test;
            return {
                canParse,
                parseResult,
            }
        }

        if (decimal) {
            if (positiveOnly) {
                reg = /^(\d+(?:\.\d+)?)(million|billion)?$/i;
            } else {
                reg = /^(-?\d+(?:\.\d+)?)(million|billion)?$/i;
            }
        } else {
            if (positiveOnly) {
                reg = /^(\d+)(million|billion)?$/i;
            } else {
                reg = /^(-?\d+)(million|billion)?$/i;
            }
        }

        let m = stringValue.match(reg);

        if (!m) {
            return {canParse}
        }

        let suffix = m[2];
        let main = m[1];
        let times = 1;

        if (/\..*0$/.test(main)) {
            return {canParse}
        }

        if (suffix) {
            if (suffix.toLowerCase() === 'million') {
                times = 1000000;
            } else {
                times = 1000000000;
            }
        }

        parseResult = parseFloat(main) * times;

        if (positiveOnly && parseResult <= 0) {
            return {
                canParse,
            }
        }

        canParse = true;

        return {
            canParse,
            parseResult,
        }
    }

    return {
        spellcheck: 'false',
        valueTransformer(stringValue, fallbackValue) {
            stringValue = getSimplified(stringValue);

            if (stringValue === '') {
                return null;
            }

            let parseResult = getParsedResult(stringValue);
            if (parseResult.canParse) {
                return parseResult.parseResult;
            }
            return fallbackValue;
        },
        valueFormatter(stringValue, fallbackValue) {
            stringValue = getSimplified(stringValue);

            if (stringValue === '') {
                return stringValue;
            }

            let parseResult = getParsedResult(stringValue);
            if (parseResult.canParse) {
                return numberWithCommas(parseResult.parseResult);
            }

            return fallbackValue;
        },
    }
};

const propsForIntTextField = getPropsForNumberField(false, false);

const propsForDecimalTextField = getPropsForNumberField(true, false);

const propsForPositiveDecimalTextField = getPropsForNumberField(true, true);

const propsForDateField = {
    spellcheck: 'false',
    valueTransformer(stringValue, fallbackValue) {
        stringValue = stringValue.trim();

        if (stringValue === '') {
            return null;
        }

        const dateFormats = [
            'YYYY-MM-DD',
            'D MMM YYYY',
            'D MMMM YYYY',
        ];
        let dateObject;

        for (let i = 0; i < dateFormats.length; i++) {
            dateObject = moment(stringValue, dateFormats[i], true);
            if (dateObject.isValid()) {
                return dateObject.unix();
            }
        }

        return fallbackValue;
    },
    valueFormatter(stringValue, fallbackValue) {
        stringValue = stringValue.trim();

        if (stringValue === '') {
            return null;
        }

        // if (!/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
        //     return fallbackValue;
        // }

        const dateFormats = [
            'YYYY-MM-DD',
            'D MMM YYYY',
            'D MMMM YYYY',
        ];
        let dateObject;

        for (let i = 0; i < dateFormats.length; i++) {
            dateObject = moment(stringValue, dateFormats[i], true);
            if (dateObject.isValid()) {
                return dateObject.format('YYYY-MM-DD');
            }
        }

        return fallbackValue;
    },
    valueToStringParser(value) {
        return dateObjectToDateString(new Date(value * 1000));
    },
    placeholder: 'e.g. ' + dateObjectToDateString(new Date()),
};

function getInputControlForField(field) {
    let component, props, primaryControl, primaryControlProps, unitControl, unitControlProps, unitProperty,
            fixedUnitValue;

    let dataFormats, leftControlLabel, leftControl, leftControlProps, leftProperty;
    let rightControlLabel, rightControl, rightControlProps, rightProperty;
    let rangeEntryType;
    let notMentionedSettable, notMentionedValue;

    switch (field.data_type) {
        case 'entity':
            component = AutocompleteBox;
            break
        case 'text':
            component = TextArea;
            break
        case 'list':
            component = ComboBox;
            props = {
                options: [{
                    value: null,
                    label: ''
                }].concat(field['all_option_values'].map(item => ({
                    value: item['option_value'],
                    label: item['option_label']
                }))),
            };
            break;
        case 'multiple_list':
            component = SelectableBoxes;
            props = {
                options: field['all_option_values'].map(item => ({
                    value: item['option_value'],
                    label: item['option_label']
                }))
            };
            break;
        case 'date':
            component = TextField;
            props = propsForDateField;
            break;
        case 'code':
            component = TextField;
            break;
        case 'decimal':
        case 'integer':
            if (field.data_type === 'decimal') {
                primaryControl = TextField;
                primaryControlProps = propsForDecimalTextField
            } else {
                primaryControl = TextField;
                primaryControlProps = propsForIntTextField
            }

            if (field.unit) {
                component = CompositeBoxForNumber;
                unitProperty = field.unit;

                switch (unitProperty) {
                    case 'percentage':
                        unitControl = TextNode;
                        fixedUnitValue = '%';
                        break;
                    case 'currency':
                        unitControl = ComboBox;
                        unitControlProps = {
                            options: [{
                                value: null,
                                label: ''
                            }].concat(currencyOptions)
                        }
                        break;
                    case 'time':
                        unitControl = ComboBox;
                        unitControlProps = {
                            options: [{
                                value: null,
                                label: ''
                            }].concat(timePeriodOptions)
                        }
                        break;
                    default:
                        // eslint-disable-next-line no-console
                        console.error('unit not registered: ', unitProperty);
                }

                props = {
                    primaryControl,
                    primaryControlProps,
                    unitControl,
                    unitControlProps,
                    unitProperty,
                    fixedUnitValue
                }
            } else {
                component = primaryControl;
                props = primaryControlProps;
            }
            break;
        case 'ratio':
            component = CompositeBoxForRatio;

            dataFormats = field['data_formats'];
            leftControlLabel = dataFormats[0]['entry_label'];
            leftControl = TextField;
            leftControlProps = propsForPositiveDecimalTextField;
            leftProperty = dataFormats[0]['entry_name'];

            rightControlLabel = dataFormats[1]['entry_label'];
            rightControl = TextField;
            rightControlProps = propsForPositiveDecimalTextField;
            rightProperty = dataFormats[1]['entry_name'];

            props = {
                leftControlLabel,
                leftControl,
                leftControlProps,
                leftProperty,
                rightControlLabel,
                rightControl,
                rightControlProps,
                rightProperty
            };
            break;
        case 'range':
            rangeEntryType = field['range_entry_type'];

            primaryControl = CompositeBoxForRange;

            leftControlLabel = 'From';
            leftProperty = 'from';
            rightControlLabel = 'To';
            rightProperty = 'to';
            leftControl = TextField;
            rightControl = TextField;

            if (rangeEntryType === 'date') {
                leftControlProps = propsForDateField;
                rightControlProps = propsForDateField;
                notMentionedSettable = true;
                notMentionedValue = placeholderDateUnix;
            } else if (rangeEntryType === 'decimal') {
                leftControlProps = propsForDecimalTextField;
                rightControlProps = propsForDecimalTextField;
            }

            primaryControlProps = {
                leftControlLabel,
                leftControl,
                leftControlProps,
                leftProperty,
                rightControlLabel,
                rightControl,
                rightControlProps,
                rightProperty,
                notMentionedSettable,
                notMentionedValue,
            };

            if (field.unit) {
                component = CompositeBoxForNumber;
                unitProperty = field.unit;

                switch (unitProperty) {
                    case 'percentage':
                        unitControl = TextNode;
                        fixedUnitValue = '%';
                        unitControlProps = {
                            style: {
                                'padding-top': '1.1rem',
                                'margin-left': '0.25rem',
                            },
                        }
                        break;
                    case 'currency':
                        unitControl = ComboBox;
                        unitControlProps = {
                            options: [{
                                value: null,
                                label: ''
                            }].concat(currencyOptions),
                            style: {
                                'padding-top': '1.1rem',
                                'margin-left': '0.25rem',
                            },
                        }
                        break;
                    case 'time':
                        unitControl = ComboBox;
                        unitControlProps = {
                            options: [{
                                value: null,
                                label: ''
                            }].concat(timePeriodOptions),
                            style: {
                                'padding-top': '1.1rem',
                                'margin-left': '0.25rem',
                            },
                        }
                        break;
                    default:
                        // eslint-disable-next-line no-console
                        console.error('unit not registered: ', unitProperty);
                }

                props = {
                    primaryControl,
                    primaryControlProps,
                    unitControl,
                    unitControlProps,
                    unitProperty,
                    fixedUnitValue
                }
            } else {
                component = primaryControl;
                props = primaryControlProps;
            }
            break;
        case 'nested_list':
            component = MultiLevelChoices;
            props = {
                all_option_values: field['all_option_values'],
                level_names: field['level_names'],
            }
            break;
        case 'multi_object':
            component = MultiObjBox;
            props = {
                maxItems: field['max_num_objects'],
                template: field['object_items'],
            }
            break;
        case 'person_capacity':
            component = PersonCapacityBox;
            break;
        case 'multiple_list_v2':
            if (field.list_object_type === 'lr') {
                component = AutocompleteBox;
                props = {
                    staticOptions: field.list_objects.map(o => {
                        return {
                            objectId: o.cat + ' - ' + o.i,
                            objectType: 'lr',
                            objectDetail: o
                        }
                    }),
                    staticOptionsFilter: (query, o) => {
                        const reg = new RegExp(escapeRegExp(query), 'gi');
                        let found = false;
                        let highlight = {};
                        if (reg.test(o.cat)) {
                            found = true;
                            highlight.cat = o.cat.replace(reg, '<span class="highlight">$&</span>');
                        }
                        if (reg.test(o.i)) {
                            found = true;
                            highlight.i = o.i.replace(reg, '<span class="highlight">$&</span>');
                        }
                        if (found) {
                            return {
                                found: true,
                                highlight
                            }
                        } else {
                            return {
                                found: false
                            }
                        }
                    }
                };
            } else {
                // eslint-disable-next-line no-console
                console.error('list object type not registered: ', JSON.parse(JSON.stringify(field)));
            }
            break;
        default:
            // eslint-disable-next-line no-console
            console.error('data type not registered: ', JSON.parse(JSON.stringify(field)));
    }
    return {
        component: component,
        props: props
    }
}

export {
    getInputControlForField,
    propsForDateField
}
